import React, { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './DonutChart.css';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels);

const DonutChart = ({
  chartConfig,
  chartTitle,
  summarizedData,
  chartData,
  previewData = [], // Add previewData for record count calculation
  selectedGroups = [] // Add selectedGroups for record count calculation
}) => {
  const [processedData, setProcessedData] = useState(null);
  const [chartOptions, setChartOptions] = useState(null);

  useEffect(() => {
    if (summarizedData && chartConfig) {
      processChartData();
    }
  }, [summarizedData, chartConfig]);

  // Helper function to convert currency/number strings to numbers (same as ScatterChart)
  const parseValue = (valueStr) => {
    if (typeof valueStr === 'number') return valueStr;
    if (typeof valueStr === 'string') {
      // Remove currency symbols and commas, then parse
      const cleanValue = valueStr.replace(/[₹,$,\s]/g, '');
      return parseFloat(cleanValue) || 0;
    }
    return 0;
  };

  // Helper function to calculate record count for a specific group (same as ChartDisplay)
  const calculateRecordCountForGroup = (groupValue, groupField) => {
    if (!previewData || previewData.length === 0 || !groupField) {
      return 0;
    }

    // Filter records that belong to this group
    const recordsInGroup = previewData.filter(row => {
      const tableName = groupField.object_name || 'main';
      const aliasedFieldName = `${tableName}_${groupField.field_name}`;
      const fieldData = row[aliasedFieldName] || row[groupField.field_name];

      let rowGroupValue = 'N/A';
      if (fieldData) {
        if (typeof fieldData === 'object' && fieldData !== null) {
          rowGroupValue = fieldData.displayValue || fieldData.value || 'N/A';
        } else {
          rowGroupValue = fieldData;
        }
      }

      return rowGroupValue === groupValue;
    });

    return recordsInGroup.length;
  };

  const processChartData = () => {
    try {
      // Get the field to slice by (pie slice field) and value field (pie value field)
      const sliceField = chartConfig.pieSliceField;
      const valueField = chartConfig.pieValueField;

      if (!sliceField || !valueField) {
        console.warn('DonutChart: Missing required fields - sliceField:', sliceField, 'valueField:', valueField);
        return;
      }

      console.log('DonutChart processing:', { sliceField, valueField, summarizedData });

      // Extract data from summarizedData using the same pattern as ScatterChart
      const dataMap = {};
      let totalValue = 0;

      console.log('summarizedData keys:', Object.keys(summarizedData));

      // Handle the nested structure of summarizedData
      Object.keys(summarizedData).forEach(groupKey => {
        if (groupKey === '_metadata') return; // Skip metadata

        const groupData = summarizedData[groupKey];
        console.log(`Processing group ${groupKey}:`, groupData);

        // Check if this group matches our slice field
        if (groupKey === sliceField) {
          Object.keys(groupData).forEach(groupValue => {
            const data = groupData[groupValue];
            console.log(`Processing group value ${groupValue}:`, data);

            // Check if this is a record count field
            let isRecordCount = false;
            if (valueField === 'record_count') {
              isRecordCount = true;
            }

            let value = 0;
            const category = groupValue; // The group value becomes our category

            if (isRecordCount) {
              // Prefer pre-aggregated record counts from summarizedData.
              // Re-counting from previewData can undercount when preview rows are already grouped.
              const preCalculated = summarizedData?.record_count?.[groupValue];
              if (preCalculated !== undefined && preCalculated !== null) {
                if (typeof preCalculated === 'object' && preCalculated.value !== undefined) {
                  value = parseValue(preCalculated.value);
                } else {
                  value = parseValue(preCalculated);
                }
                console.log(`Using summarized record count for ${category}: ${value}`);
              } else {
                const metadata = summarizedData._metadata;
                const groupField = metadata.group_fields[sliceField];
                value = calculateRecordCountForGroup(groupValue, groupField);
                console.log(`Calculated record count for ${category}: ${value}`);
              }
            } else {
              // Extract field name from the dropdown selection (same as ScatterChart)
              // The dropdown shows "Sum of Salary" but we need "salary"
              const valueFieldName = valueField.toLowerCase().split(' ')[0]; // "Salary (SUM)" -> "salary"

              console.log(`Looking for valueField '${valueFieldName}':`, data[valueFieldName]);
              console.log(`Available fields in data:`, Object.keys(data));
              console.log(`Full data object:`, data);

              if (data && data[valueFieldName] && data[valueFieldName].value !== undefined) {
                const rawValue = data[valueFieldName].value;
                value = parseValue(rawValue); // Use parseValue to handle currency symbols

                console.log(`Found data: category='${category}', rawValue='${rawValue}', parsedValue=${value}`);
              } else {
                console.log(`Skipping group value ${groupValue} - missing valueField '${valueFieldName}'`);
                return; // Skip this iteration
              }
            }

            // Add value to dataMap
            if (dataMap[category]) {
              dataMap[category] += value;
            } else {
              dataMap[category] = value;
            }

            totalValue += value;
          });
        } else {
          console.log(`Skipping group ${groupKey} - not matching sliceField '${sliceField}'`);
        }
      });

      console.log('Final dataMap:', dataMap);

      // Convert to arrays for Chart.js
      const labels = Object.keys(dataMap);
      const values = Object.values(dataMap);

      if (labels.length === 0) {
        console.warn('DonutChart: No data found to display');
        return;
      }

      // Generate colors, using custom legend colors if available
      const colors = generateColors(labels.length, chartConfig.legendColors, labels);

      // Prepare dataset
      const dataset = {
        label: valueField,
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(color => color.replace('0.8', '1')),
        borderWidth: 1,
      };

      setProcessedData({
        labels: labels,
        datasets: [dataset]
      });

      // Prepare chart options
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 8, right: 12, bottom: 8, left: 12 },
        },
        plugins: {
          title: {
            display: !!(chartConfig.title || chartTitle),
            text: chartConfig.title || chartTitle || 'Pie Chart',
            font: {
              size: 16,
              weight: 'bold'
            }
          },
          legend: {
            display: true,
            position: chartConfig.legendPosition || 'bottom',
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            enabled: chartConfig.showTooltips !== undefined ? chartConfig.showTooltips : true,
            callbacks: {
              label: function (context) {
                const label = context.label || '';
                const value = context.parsed;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);

                return `${label}: ${formatValue(value, chartConfig.valueFieldType)} (${percentage}%)`;
              }
            }
          },
          datalabels: {
            display: chartConfig.showValues || chartConfig.showPercentages || false,
            color: chartConfig.labelColor || '#000000',
            font: {
              size: 11,
              weight: '600'
            },
            /* Outside labels force Chart.js to shrink the arc area; center keeps full width usage */
            align: 'center',
            anchor: 'center',
            offset: 0,
            padding: 2,
            clamp: true,
            clip: true,
            formatter: (value, context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);

              // Only show if percentage is significant (> 3%)
              if (parseFloat(percentage) < 3) {
                return '';
              }

              let label = '';

              if (chartConfig.showValues && chartConfig.showPercentages) {
                const formattedValue = formatValue(value, chartConfig.valueFieldType);
                label = `${formattedValue}\n${percentage}%`;
              } else if (chartConfig.showValues) {
                // Show only value
                label = formatValue(value, chartConfig.valueFieldType);
              } else if (chartConfig.showPercentages) {
                // Show only percentage
                label = `${percentage}%`;
              }

              return label;
            }
          }
        }
      };

      setChartOptions(options);

    } catch (error) {
      console.error('Error processing donut chart data:', error);
    }
  };

  // Convert hex to rgba for chart display
  const hexToRgba = (hex, alpha = 0.8) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const generateColors = (count, legendColors = {}, labels = []) => {
    // Use the same base colors as ReportChart.js for consistency
    const baseColors = [
      'rgba(54, 162, 235, 0.8)',   // Blue
      'rgba(255, 99, 132, 0.8)',   // Red
      'rgba(255, 205, 86, 0.8)',   // Yellow
      'rgba(75, 192, 192, 0.8)',   // Teal
      'rgba(153, 102, 255, 0.8)',  // Purple
      'rgba(255, 159, 64, 0.8)',   // Orange
      'rgba(199, 199, 199, 0.8)',  // Grey
      'rgba(83, 102, 255, 0.8)',   // Indigo
      'rgba(255, 99, 255, 0.8)',   // Pink
      'rgba(99, 255, 132, 0.8)',   // Green
    ];

    const colors = [];
    for (let i = 0; i < count; i++) {
      const label = labels[i];
      // Use custom color if available, otherwise use base color
      if (legendColors[label]) {
        // Convert hex to rgba if it's a hex color
        const customColor = legendColors[label];
        if (customColor.startsWith('#')) {
          colors.push(hexToRgba(customColor));
        } else {
          colors.push(customColor);
        }
      } else {
        colors.push(baseColors[i % baseColors.length]);
      }
    }
    return colors;
  };

  const formatValue = (value, dataType) => {
    if (value === null || value === undefined || isNaN(value)) return '0';

    switch (dataType) {
      case 'currency':
        return `₹${parseFloat(value).toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`;
      case 'number':
      case 'decimal':
        return parseFloat(value).toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      case 'percent':
        return `${parseFloat(value).toFixed(2)}%`;
      default:
        return parseFloat(value).toLocaleString('en-IN');
    }
  };

  // Check if required fields are missing
  const missingFields = [];
  if (!chartConfig?.pieValueField) missingFields.push('Value');
  if (!chartConfig?.pieSliceField) missingFields.push('Sliced By');

  if (missingFields.length > 0) {
    return (
      <div className="donut-chart-container">
        <div className="chart-loading">
          <p>Please select {missingFields.join(' and ')} to display the donut chart</p>
        </div>
      </div>
    );
  }

  if (!processedData || !chartOptions) {
    return (
      <div className="donut-chart-container">
        <div className="chart-loading">
          <p>Loading donut chart...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="donut-chart-container">
      <div className="donut-chart-wrapper">
        <div className="doughnut-chart-container">
          <Doughnut data={processedData} options={chartOptions} />
          {chartConfig.showTotal && (
            <div className="center-total">
              {/* <div className="total-label">Total</div> */}
              <div className="total-value">
                {formatValue(
                  processedData.datasets[0].data.reduce((a, b) => a + b, 0),
                  chartConfig.valueFieldType
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonutChart;
